import dayjs from 'dayjs';

import type {
  ReplaceWhitelistPayload,
  WhitelistDraft,
  WhitelistEntry,
  WhitelistResponse,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function formatDateTime(value?: string | null) {
  if (!value) return '–';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '–';
  return parsed.format('YYYY-MM-DD HH:mm');
}

export function shortenAddress(address?: string | null) {
  if (!address) return '';
  const s = String(address);
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function normalizeEntry(raw: unknown): WhitelistEntry | null {
  if (!isRecord(raw)) return null;

  return {
    userId: String(raw.userId ?? raw.user_id ?? ''),
    address: String(raw.address ?? ''),
    reason: String(raw.reason ?? ''),
    addedBy: String(raw.addedBy ?? raw.added_by ?? ''),
    addedAt: String(raw.addedAt ?? raw.added_at ?? ''),
  };
}

export function normalizeWhitelist(raw: unknown): WhitelistResponse {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  if (!isRecord(source)) {
    return { items: [], totalCount: 0 };
  }

  const items = ensureArray<unknown>(source.items)
    .map(normalizeEntry)
    .filter((item): item is WhitelistEntry => Boolean(item));

  const totalCount = Number(source.totalCount ?? source.total_count ?? items.length) || items.length;

  return { items, totalCount };
}

let localIdCounter = 0;
function nextLocalId() {
  localIdCounter += 1;
  return `wl-${Date.now()}-${localIdCounter}`;
}

export function toDrafts(items: WhitelistEntry[]): WhitelistDraft[] {
  return items.map((item) => ({
    localId: nextLocalId(),
    userId: item.userId,
    reason: item.reason,
    address: item.address,
    addedBy: item.addedBy,
    addedAt: item.addedAt,
  }));
}

export function createEmptyDraft(): WhitelistDraft {
  return {
    localId: nextLocalId(),
    userId: '',
    reason: '',
    address: '',
    addedBy: '',
    addedAt: '',
  };
}

export function buildReplacePayload(drafts: WhitelistDraft[]): ReplaceWhitelistPayload {
  return {
    items: drafts.map((draft) => ({
      userId: draft.userId.trim(),
      reason: draft.reason.trim(),
    })),
  };
}

export function isWhitelistEqual(a: WhitelistDraft[], b: WhitelistDraft[]) {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return (
      other &&
      item.userId === other.userId &&
      item.reason === other.reason
    );
  });
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (isRecord(error) && typeof error.message === 'string') return error.message;
  return String(error);
}
